import { useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { BreadcrumbSchema } from "@/components/StructuredData";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

// Static data moved outside component to prevent re-creation on each render
const PROJECTS = [
  { id: 1, title: "Loft Conversion", location: "Surrey", category: "Loft Conversion", description: "Complete loft conversion creating a spacious new living space.", image: "/images/projects/Loft1.JPG" },
  { id: 2, title: "Loft Space", location: "Surrey", category: "Loft Conversion", description: "Beautiful loft transformation with natural light.", image: "/images/projects/Loft2.JPG" },
  { id: 3, title: "Modern Kitchen", location: "SW London", category: "Kitchen Fitting", description: "Complete kitchen renovation with custom cabinetry.", image: "/images/projects/Kitchen1.JPG" },
  { id: 4, title: "Kitchen Installation", location: "Surrey", category: "Kitchen Fitting", description: "Bespoke kitchen fitting with quality finishes.", image: "/images/projects/Kitchen2.JPG" },
  { id: 5, title: "Kitchen Refurbishment", location: "SW London", category: "Kitchen Fitting", description: "Kitchen refurbishment with modern units.", image: "/images/projects/Kitchen3.JPG" },
  { id: 6, title: "House Extension", location: "Surrey", category: "House Extension", description: "Rear extension with seamless indoor-outdoor living.", image: "/images/projects/Extension1.JPG" },
  { id: 7, title: "Extension Project", location: "Surrey", category: "House Extension", description: "Quality home extension with expert finish.", image: "/images/projects/Extension2.JPG" },
  { id: 8, title: "New Build Extension", location: "SW London", category: "House Extension", description: "Beautiful extension adding valuable living space.", image: "/images/projects/Extension3.JPG" },
  { id: 9, title: "Fitted Wardrobes", location: "SW London", category: "Fitted Wardrobes", description: "Floor-to-ceiling fitted wardrobes with soft-close drawers.", image: "/images/projects/Wardrobe1.JPG" },
  { id: 10, title: "Bespoke Storage", location: "Surrey", category: "Fitted Wardrobes", description: "Custom wardrobes maximizing every inch of space.", image: "/images/projects/Wardrobe2.JPG" },
  { id: 11, title: "Bathroom Renovation", location: "Surrey", category: "Bathroom", description: "Full bathroom renovation with modern fixtures.", image: "/images/projects/Bathroom1.JPG" },
  { id: 12, title: "Bathroom Refurb", location: "SW London", category: "Bathroom", description: "Contemporary bathroom with spa-like finish.", image: "/images/projects/Bathroom2.JPG" },
  { id: 13, title: "Roofing Work", location: "Surrey", category: "Roofing", description: "Professional roofing with quality materials.", image: "/images/projects/Roof1.JPG" },
  { id: 14, title: "Roof Installation", location: "Surrey", category: "Roofing", description: "Durable roof installation built to last.", image: "/images/projects/Roof2.JPG" }
];

const CATEGORIES = ["All", "Loft Conversion", "Kitchen Fitting", "House Extension", "Fitted Wardrobes", "Bathroom", "Roofing"];

const BREADCRUMB_ITEMS = [
  { name: "Home", url: "/" },
  { name: "Projects", url: "/projects" }
];

const ProjectsPage = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredProjects = selectedCategory === "All" 
    ? PROJECTS 
    : PROJECTS.filter(p => p.category === selectedCategory);

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO 
        title="Project Portfolio | TJP Construction Surrey"
        description="Browse completed projects by TJP Construction across Surrey & SW London. Extensions, loft conversions, kitchens & renovations. See our work."
        keywords="carpentry projects surrey, construction portfolio, loft conversion examples, kitchen fitting photos, house extension gallery"
        url="/projects"
      />
      <BreadcrumbSchema items={BREADCRUMB_ITEMS} />
      <Header />

      <section className="bg-gradient-to-br from-stone-100 to-stone-200 py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <p className="text-sm font-medium tracking-wide uppercase text-amber-700 mb-4">Our Work</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-6">Recent Projects</h1>
          <p className="text-lg text-stone-600 max-w-3xl mx-auto">Browse our portfolio of completed projects across Surrey and SW London.</p>
        </div>
      </section>

      <section className="py-8 bg-white border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-wrap gap-3 justify-center">
            {CATEGORIES.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === category ? "bg-slate-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-lg overflow-hidden shadow-xl shadow-stone-200/50 group cursor-pointer hover:shadow-2xl transition-shadow"
                onClick={() => setSelectedProject(project)}
              >
                <div className="relative h-64 overflow-hidden bg-stone-200">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                    width={400}
                    height={256}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                    <span className="text-white font-medium">View Project</span>
                  </div>
                </div>
                <div className="p-6">
                  <span className="text-amber-700 text-sm font-medium">{project.category}</span>
                  <h3 className="text-xl font-bold text-slate-900 mt-1 mb-2">{project.title}</h3>
                  <p className="text-stone-500 text-sm">{project.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <button onClick={() => setSelectedProject(null)} className="absolute top-6 right-6 text-white hover:text-amber-500 transition-colors z-50">
            <X size={32} />
          </button>
          <div className="max-w-4xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <img src={selectedProject.image} alt={selectedProject.title} className="rounded-lg w-full h-auto max-h-[70vh] object-contain" />
            </div>
            <div className="text-white space-y-4">
              <span className="text-amber-500 text-sm font-medium">{selectedProject.category}</span>
              <h2 className="text-3xl font-bold">{selectedProject.title}</h2>
              <p className="text-stone-300">{selectedProject.location}</p>
              <p className="text-stone-300 leading-relaxed">{selectedProject.description}</p>
            </div>
          </div>
        </div>
      )}

      <section className="py-20 md:py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Start Your Project?</h2>
          <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">Get a free, no-obligation estimate today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/">
              <Button className="bg-amber-700 text-white hover:bg-amber-600 h-14 px-10 font-bold">Get Free Estimate<ArrowRight className="ml-2" size={20} /></Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="h-14 px-8 font-semibold border-2 border-white text-white hover:bg-white hover:text-slate-900">Contact Us</Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
